<script>
import { useDebounceFn } from '@vueuse/core';
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner.vue';
import CampaignContactsAPI from 'dashboard/api/campaignContacts';

const CONTACT_FILTER_FIELDS = [
  { value: 'name', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.NAME' },
  { value: 'email', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.EMAIL' },
  { value: 'phone_number', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.PHONE' },
  { value: 'identifier', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.IDENTIFIER' },
  { value: 'company', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.COMPANY' },
  { value: 'city', labelKey: 'CAMPAIGN.ADD.CONTACTS.FIELDS.CITY' },
];

export default {
  name: 'CampaignContactsSelector',
  components: {
    Spinner,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['input', 'update:value', 'selection-change'],
  data() {
    return {
      contacts: [],
      contactsMeta: {
        current_page: 0,
        total_pages: 1,
        total_count: 0,
      },
      contactsLoading: false,
      contactsLoadingMore: false,
      contactFilters: {
        search: '',
        labelIds: [],
        field: 'name',
        fieldValue: '',
      },
      contactSearchInput: '',
      selectedContactsMap: {},
      contactsPerPage: 25,
      scrollObserver: null,
      scrollSentinel: null,
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
    }),
    selectedContacts() {
      return Object.values(this.selectedContactsMap);
    },
    selectedCount() {
      return this.selectedContacts.length;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    filterFields() {
      return CONTACT_FILTER_FIELDS;
    },
    areAllLoadedContactsSelected() {
      if (!this.contacts.length) return false;
      return this.contacts.every(contact => this.isContactSelected(contact.id));
    },
    areSomeLoadedContactsSelected() {
      if (!this.contacts.length) return false;
      return this.contacts.some(contact => this.isContactSelected(contact.id));
    },
    hasMoreContacts() {
      return this.contactsMeta.current_page < this.contactsMeta.total_pages;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        const map = {};
        (newValue || []).forEach(contact => {
          map[contact.id] = contact;
        });
        this.selectedContactsMap = map;
      }
    },
    areSomeLoadedContactsSelected: {
      handler() {
        this.updateSelectAllCheckboxState();
      },
      immediate: true,
    },
    areAllLoadedContactsSelected: {
      handler() {
        this.updateSelectAllCheckboxState();
      },
      immediate: true,
    },
    'contactFilters.labelIds': {
      handler() {
        this.resetAndFetchContacts();
      },
      deep: true,
    },
    'contactFilters.field'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.contactFilters.fieldValue = '';
        this.resetAndFetchContacts();
      }
    },
    contacts() {
      this.updateSelectAllCheckboxState();
    },
  },
  created() {
    this.debouncedContactSearch = useDebounceFn(value => {
      this.contactFilters.search = value;
      this.resetAndFetchContacts();
    }, 300);
    this.debouncedFieldValueSearch = useDebounceFn(value => {
      this.contactFilters.fieldValue = value;
      this.resetAndFetchContacts();
    }, 300);
  },
  mounted() {
    if (!this.labels.length) {
      this.$store.dispatch('labels/get');
    }
    this.fetchContacts();
    this.setupScrollObserver();
  },
  beforeUnmount() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    if (this.scrollSentinel && this.scrollSentinel.parentNode) {
      this.scrollSentinel.parentNode.removeChild(this.scrollSentinel);
    }
  },
  methods: {
    hasNameOrPhone(contact) {
      const name = (contact?.name || '').trim();
      const phone = (contact?.phone_number || '').trim();
      return Boolean(name || phone);
    },
    resetAndFetchContacts() {
      this.contacts = [];
      this.contactsMeta = { current_page: 0, total_pages: 1, total_count: 0 };
      this.fetchContacts();
    },
    async fetchContacts() {
      if (this.contactsLoading || this.contactsLoadingMore) return;
      
      const nextPage = this.contactsMeta.current_page + 1;
      
      if (nextPage === 1) {
        this.contactsLoading = true;
      } else {
        this.contactsLoadingMore = true;
      }

      try {
        const params = {
          page: nextPage,
          per_page: this.contactsPerPage,
          search: this.contactFilters.search || undefined,
          field: this.contactFilters.field || undefined,
          field_value: this.contactFilters.fieldValue || undefined,
          label_ids: this.contactFilters.labelIds.map(label => label.id || label).filter(Boolean)
        };
        const { data } = await CampaignContactsAPI.list(params);
        const newContacts = (data.contacts || []).filter(contact =>
          this.hasNameOrPhone(contact)
        );
        
        // Adicionar novos contatos à lista existente
        this.contacts = [...this.contacts, ...newContacts];
        this.contactsMeta = data.meta || { current_page: nextPage, total_pages: 1, total_count: 0 };
        
        // Atualizar contatos selecionados se já existirem
        newContacts.forEach(contact => {
          if (this.selectedContactsMap[contact.id]) {
            this.selectedContactsMap[contact.id] = contact;
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.contactsLoading = false;
        this.contactsLoadingMore = false;
        this.emitSelection();
      }
    },
    setupScrollObserver() {
      this.$nextTick(() => {
        const scrollContainer = this.$el.querySelector('.contacts-scroll-container');
        if (!scrollContainer) return;

        const observerOptions = {
          root: scrollContainer,
          rootMargin: '100px',
          threshold: 0.1,
        };

        const observerCallback = (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && this.hasMoreContacts && !this.contactsLoadingMore && !this.contactsLoading) {
              this.fetchContacts();
            }
          });
        };

        this.scrollObserver = new IntersectionObserver(observerCallback, observerOptions);
        
        // Observar o último elemento da tabela (ou o elemento de loading)
        this.$watch('contacts', () => {
          this.$nextTick(() => {
            const tbody = scrollContainer.querySelector('tbody');
            if (!tbody) return;
            
            // Remover observação anterior
            if (this.scrollSentinel) {
              this.scrollObserver.unobserve(this.scrollSentinel);
              if (this.scrollSentinel.parentNode) {
                this.scrollSentinel.parentNode.removeChild(this.scrollSentinel);
              }
            }
            
            // Criar novo sentinela no final do tbody
            const sentinel = document.createElement('tr');
            sentinel.className = 'scroll-sentinel';
            sentinel.innerHTML = '<td colspan="6" style="height: 1px;"></td>';
            tbody.appendChild(sentinel);
            
            this.scrollSentinel = sentinel;
            this.scrollObserver.observe(sentinel);
          });
        }, { immediate: true });
      });
    },
    emitSelection() {
      const contacts = this.selectedContacts;
      this.$emit('input', contacts);
      this.$emit('update:value', contacts);
      this.$emit('selection-change', contacts);
    },
    toggleContact(contact) {
      if (this.selectedContactsMap[contact.id]) {
        delete this.selectedContactsMap[contact.id];
      } else {
        this.selectedContactsMap[contact.id] = contact;
      }
      this.emitSelection();
      this.updateSelectAllCheckboxState();
    },
    isContactSelected(contactId) {
      return Boolean(this.selectedContactsMap[contactId]);
    },
    removeSelectedContact(contactId) {
      delete this.selectedContactsMap[contactId];
      this.emitSelection();
    },
    onContactSearchInput(event) {
      const value = event.target.value;
      this.contactSearchInput = value;
      this.debouncedContactSearch(value);
    },
    onContactFieldValueInput(event) {
      const value = event.target.value;
      this.debouncedFieldValueSearch(value);
    },
    contactFieldLabel(field) {
      const option = this.filterFields.find(item => item.value === field);
      if (!option) {
        return field;
      }
      return this.$t(option.labelKey);
    },
    contactDisplayValue(contact) {
      return contact.name || contact.phone_number || contact.email || contact.identifier || contact.id;
    },
    toggleSelectAll() {
      const allSelected = this.areAllLoadedContactsSelected;
      this.contacts.forEach(contact => {
        if (allSelected) {
          // Deselecionar todos os contatos carregados
          if (this.isContactSelected(contact.id)) {
            delete this.selectedContactsMap[contact.id];
          }
        } else {
          // Selecionar todos os contatos carregados
          if (!this.isContactSelected(contact.id)) {
            this.selectedContactsMap[contact.id] = contact;
          }
        }
      });
      this.emitSelection();
    },
    updateSelectAllCheckboxState() {
      this.$nextTick(() => {
        if (this.$refs.selectAllCheckbox) {
          const isIndeterminate = this.areSomeLoadedContactsSelected && !this.areAllLoadedContactsSelected;
          this.$refs.selectAllCheckbox.indeterminate = isIndeterminate;
        }
      });
    },
  },
};
</script>

<template>
  <div class="campaign-contacts-selector space-y-4">
    <div
      class="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/50 p-4 space-y-4"
    >
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.SEARCH_LABEL') }}
        </label>
        <input
          :value="contactSearchInput"
          type="text"
          class="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm shadow-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500"
          :placeholder="$t('CAMPAIGN.ADD.CONTACTS.SEARCH_PLACEHOLDER')"
          @input="onContactSearchInput"
        />
      </div>
      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.FIELD_FILTER_LABEL') }}
        </label>
        <select
          v-model="contactFilters.field"
          class="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm shadow-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500"
        >
          <option
            v-for="option in filterFields"
            :key="option.value"
            :value="option.value"
          >
            {{ $t(option.labelKey) }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1.5 min-w-0">
        <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.FIELD_VALUE_LABEL') }}
        </label>
        <input
          class="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 text-sm shadow-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-woot-500/40 focus:border-woot-500"
          :placeholder="$t('CAMPAIGN.ADD.CONTACTS.FIELD_VALUE_PLACEHOLDER')"
          :value="contactFilters.fieldValue"
          @input="onContactFieldValueInput"
        />
      </div>

      <div class="flex flex-col gap-1.5 min-w-0 pt-4 mt-1 border-t border-slate-200/80 dark:border-slate-700/80 md:col-span-3">
        <label class="text-xs font-medium text-slate-700 dark:text-slate-200">
          {{ $t('CAMPAIGN.ADD.CONTACTS.TAG_FILTER_LABEL') }}
        </label>
        <div class="multiselect-wrap--small [&_.multiselect__tags]:min-h-10 [&_.multiselect__tags]:rounded-lg [&_.multiselect__tags]:border-slate-300 [&_.multiselect__tags]:dark:border-slate-600 [&_.multiselect__tags]:bg-white [&_.multiselect__tags]:dark:bg-slate-900 [&_.multiselect__tags]:shadow-sm">
          <multiselect
            v-model="contactFilters.labelIds"
            :options="labels"
            track-by="id"
            label="title"
            :multiple="true"
            :searchable="true"
            :close-on-select="false"
            :show-labels="false"
            :placeholder="$t('CAMPAIGN.ADD.CONTACTS.TAG_FILTER_PLACEHOLDER')"
          >
            <template #option="{ option }">
              <span class="flex items-center gap-2 min-w-0">
                <span
                  class="size-2.5 shrink-0 rounded-full"
                  :style="{ backgroundColor: option.color }"
                />
                <span class="truncate">{{ option.title }}</span>
              </span>
            </template>
            <template #tag="{ option, remove }">
              <span
                class="inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-200"
              >
                <span
                  class="size-2 shrink-0 rounded-full"
                  :style="{ backgroundColor: option.color }"
                />
                {{ option.title }}
                <button
                  type="button"
                  class="ml-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 leading-none"
                  @click="remove(option)"
                >
                  ×
                </button>
              </span>
            </template>
          </multiselect>
        </div>
      </div>
    </div>
    </div>

    <div class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm contacts-scroll-container max-h-[480px] overflow-y-auto">
      <table class="min-w-full text-sm">
        <thead class="bg-slate-50 dark:bg-slate-800 text-left sticky top-0 z-10">
          <tr>
            <th class="px-4 py-2 w-10">
              <input
                v-if="!contactsLoading && contacts.length > 0"
                ref="selectAllCheckbox"
                type="checkbox"
                class="woot-checkbox"
                :checked="areAllLoadedContactsSelected"
                @change="toggleSelectAll"
                :title="$t('CAMPAIGN.ADD.CONTACTS.SELECT_ALL')"
              />
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.NAME') }}
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.COMPANY') }}
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.CITY') }}
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.EMAIL') }}
            </th>
            <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.COLUMNS.PHONE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="contactsLoading && !contacts.length">
            <td colspan="6" class="px-4 py-6 text-center">
              <Spinner size="20" class="inline-block" />
              <span class="ml-2 text-sm text-slate-600 dark:text-slate-300">
                {{ $t('CAMPAIGN.ADD.CONTACTS.LOADING') }}
              </span>
            </td>
          </tr>
          <tr v-else-if="!contactsLoading && !contacts.length">
            <td colspan="6" class="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-300">
              {{ $t('CAMPAIGN.ADD.CONTACTS.NO_RESULTS') }}
            </td>
          </tr>
          <template v-else>
            <tr
              v-for="contact in contacts"
              :key="contact.id"
              class="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
            >
            <td class="px-4 py-2">
              <input
                type="checkbox"
                class="woot-checkbox"
                :checked="isContactSelected(contact.id)"
                @change="toggleContact(contact)"
              />
            </td>
            <td class="px-4 py-2 text-slate-700 dark:text-slate-200">
              {{ contact.name || '—' }}
            </td>
            <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ contact.company || '—' }}
            </td>
            <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ contact.city || '—' }}
            </td>
            <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ contact.email || '—' }}
            </td>
            <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
              {{ contact.phone_number || '—' }}
            </td>
          </tr>
          </template>
          <tr v-if="contactsLoadingMore">
            <td colspan="6" class="px-4 py-4 text-center">
              <Spinner size="16" class="inline-block" />
              <span class="ml-2 text-xs text-slate-500 dark:text-slate-400">
                {{ $t('CAMPAIGN.ADD.CONTACTS.LOADING_MORE') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
      <span>
        {{ $t('CAMPAIGN.ADD.CONTACTS.SELECTED_COUNT', { count: selectedCount }) }}
      </span>
      <span v-if="contacts.length > 0" class="text-slate-500 dark:text-slate-400">
        {{ $t('CAMPAIGN.ADD.CONTACTS.LOADED_COUNT', { loaded: contacts.length, total: contactsMeta.total_count || contacts.length }) }}
      </span>
    </div>

    <div v-if="selectedContacts.length" class="flex flex-wrap gap-2 mt-2">
      <span
        v-for="contact in selectedContacts"
        :key="`selected-contact-${contact.id}`"
        class="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 flex items-center gap-2"
      >
        {{ contactDisplayValue(contact) }}
        <button
          type="button"
          class="text-slate-500 hover:text-red-500"
          @click.prevent="removeSelectedContact(contact.id)"
        >
          <fluent-icon icon="dismiss-circle" size="12" />
        </button>
      </span>
    </div>

    <p class="text-xs text-slate-500 dark:text-slate-400">
      {{ $t('CAMPAIGN.ADD.CONTACTS.HELP') }}
    </p>
  </div>
</template>
